/* @jsx h */
import { defineComponent, ref, h, computed, watch } from "vue";
import { useMutation } from "@tanstack/vue-query";
import { useRoute, useRouter } from "vue-router";
import { Calendar, LemmonModal, useLocalization } from "@lemmonhq/uikit";
import {
  createReservationRequest,
  fetchReservationRequest,
  fetchReservationTags,
} from "@app/server/client";
import { useCustomer } from "@app/vue/providers/Auth";
import { useMenu, useTRPC } from "@app/vue/providers/index";
import { PartySizeSelector } from "../components/PartySizeSelector";
import { ServiceTimeSelector } from "../components/ServiceTimeSelector";
// ContactForm fields are now inlined for cleaner layout
import {
  getMinPartySize,
  getMaxPartySize,
  getReservationsNote,
  getReservationSchedule,
  getReservationOpenState,
  getTimeSlotMinutes,
  getReservationDurationMinutes,
  getReservationsRequireDeposit,
  type NextOpeningInfo,
} from "../utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  getTodayInTimezone,
  formatDateInTimezone,
  formatPrice,
  formatTimeInTimezone,
  daysOfWeek,
} from "@lemmonhq/orders";
import type { ReservationRequest } from "@lemmonhq/types";
import { getEmailTypoSuggestion } from "@lemmonhq/blogic";

dayjs.extend(utc);
dayjs.extend(timezone);

export const BookReservations = defineComponent({
  name: "BookReservations",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const trpc = useTRPC();
    const { shop, reservationForm } = useMenu();
    const { customer, showAuthDialog } = useCustomer();
    const { t } = useLocalization();
    const shopSlug = computed(() => route.params.shopslug as string);
    const currentRequestId = computed(() => {
      const value = route.query.current_request_id;
      return typeof value === "string" ? value : null;
    });
    const currentRequest = ref<
      (ReservationRequest & { reservation?: any }) | null
    >(null);
    const currentRequestError = ref<string | null>(null);
    const prefilledRequestId = ref<string | null>(null);

    const createIntegratedWorkflowMutation = useMutation({
      mutationFn: (input: {
        requestedTime: string;
        partySize: number;
        durationMins: number;
        notes?: string;
        partyName: string;
        partyEmail: string;
        partyPhone: string;
        tagIds?: string[];
        requestKind?: "new" | "edit";
        targetReservationId?: string;
      }) =>
        trpc.integratedCustomerPaymentWorkflow.create.mutate({
          subjectType: "reservation_deposit",
          shopSlug: shopSlug.value,
          ...input,
        }),
    });

    // Tags
    const availableTags = ref<
      {
        id: string;
        name: string;
        color?: string | null;
        icon?: string | null;
      }[]
    >([]);

    const loadTags = async () => {
      try {
        const tags = await fetchReservationTags(shopSlug.value);
        availableTags.value = tags;
      } catch (e) {
        console.error("Failed to load reservation tags:", e);
      }
    };

    const getTagTextColor = (bgColor: string): string => {
      if (!bgColor) return "#ffffff";
      if (!/^#[0-9a-fA-F]{3,8}$/.test(bgColor)) return "#1a1a1a";
      const hex = bgColor.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? "#1a1a1a" : "#ffffff";
    };

    const toggleTag = (tagId: string) => {
      const idx = reservationForm.selectedTagIds.indexOf(tagId);
      if (idx >= 0) {
        reservationForm.selectedTagIds.splice(idx, 1);
      } else {
        reservationForm.selectedTagIds.push(tagId);
      }
    };

    // Load tags when shop is available
    watch(
      () => shop.value,
      (s) => {
        if (s) loadTags();
      },
      { immediate: true },
    );

    // Initialize selectedDate with restaurant's timezone when shop loads
    watch(
      () => shop.value?.timezone,
      (timezone) => {
        if (timezone && !reservationForm.selectedDate) {
          reservationForm.selectedDate = getTodayInTimezone(timezone);
        }
      },
      { immediate: true },
    );

    const hydrateFormFromCurrentRequest = (
      request: ReservationRequest & { reservation?: any },
    ) => {
      const timezoneName = shop.value?.timezone || "UTC";
      const activeTime = request.reservation?.time || request.requested_time;
      const localized = dayjs(activeTime).tz(timezoneName);

      reservationForm.name = request.party_name || "";
      reservationForm.email = request.party_email || "";
      reservationForm.phone = request.party_phone || "";
      reservationForm.notes = request.notes || "";
      reservationForm.partySize = request.party_size || 2;
      reservationForm.selectedDate = localized.toDate();
      reservationForm.selectedTime = "";
      reservationForm.selectedService = null;
      reservationForm.selectedTagIds =
        request.requested_tags?.map((tag) => tag.id) || [];
    };

    watch(
      [shopSlug, currentRequestId, () => shop.value?.timezone],
      async ([slug, requestId, timezoneName]) => {
        if (!slug || !requestId || !timezoneName) {
          currentRequest.value = null;
          currentRequestError.value = null;
          return;
        }

        if (prefilledRequestId.value === requestId && currentRequest.value) {
          return;
        }

        try {
          currentRequestError.value = null;
          const request = await fetchReservationRequest(slug, requestId);
          currentRequest.value = request;
          hydrateFormFromCurrentRequest(request);
          prefilledRequestId.value = requestId;
        } catch (error) {
          console.error("Failed to load current reservation request:", error);
          currentRequest.value = null;
          currentRequestError.value = t(
            "customer.reserve.current_request_load_failed",
          );
        }
      },
      { immediate: true },
    );

    // Get settings from shop
    const settings = computed(() => shop.value?.settings);
    const reservationSchedule = computed(() =>
      getReservationSchedule(settings.value),
    );
    const minPartySize = computed(() => getMinPartySize(settings.value));
    const maxPartySize = computed(() => getMaxPartySize(settings.value));
    const reservationsNote = computed(() =>
      getReservationsNote(settings.value),
    );
    const timeSlotMinutes = computed(() => getTimeSlotMinutes(settings.value));
    const reservationDurationMinutes = computed(() =>
      getReservationDurationMinutes(settings.value),
    );
    const requiresDeposit = computed(() =>
      getReservationsRequireDeposit(settings.value),
    );

    // Helper to get day name from date
    const getDayName = (date: Date): string => {
      const days = daysOfWeek;
      return days[date.getDay()];
    };

    // Calculate prepayment amount for selected time
    const prepaymentAmount = computed(() => {
      if (!requiresDeposit.value) return null;
      if (!reservationForm.selectedDate || !reservationForm.selectedTime)
        return null;

      const dayName = getDayName(reservationForm.selectedDate);
      const daySchedule = reservationSchedule.value[dayName];

      if (!daySchedule || daySchedule.length === 0) return null;

      // Parse selected time to minutes
      const [selectedHour, selectedMinute] = reservationForm.selectedTime
        .split(":")
        .map(Number);
      const selectedTimeMinutes = selectedHour * 60 + selectedMinute;

      // Find the schedule range that contains the selected time
      for (const range of daySchedule) {
        const [startHour, startMin] = range.start_time.split(":").map(Number);
        const [endHour, endMin] = range.end_time.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (
          selectedTimeMinutes >= startMinutes &&
          selectedTimeMinutes < endMinutes
        ) {
          return range.pre_payment || null;
        }
      }

      return null;
    });

    // Email validation helper
    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    const emailTypoSuggestion = computed(() =>
      getEmailTypoSuggestion(reservationForm.email),
    );

    // Validation errors
    const validationErrors = computed(() => {
      const errors: string[] = [];

      if (!reservationForm.name.trim()) {
        errors.push(t("customer.reserve.validation_name_required"));
      }

      if (!reservationForm.email.trim()) {
        errors.push(t("customer.reserve.validation_email_required"));
      } else if (emailTypoSuggestion.value) {
        errors.push(
          t("customer.reserve.validation_email_typo", [
            emailTypoSuggestion.value,
          ]),
        );
      } else if (!isValidEmail(reservationForm.email)) {
        errors.push(t("customer.reserve.validation_email_invalid"));
      }

      if (!reservationForm.phone.trim()) {
        errors.push(t("customer.reserve.validation_phone_required"));
      }

      if (!reservationForm.selectedDate) {
        errors.push(t("customer.reserve.validation_date_required"));
      }

      if (!reservationForm.selectedTime) {
        errors.push(t("customer.reserve.validation_time_required"));
      }

      if (reservationForm.partySize < minPartySize.value) {
        errors.push(
          t("customer.reserve.validation_party_size_min", [minPartySize.value]),
        );
      }

      return errors;
    });

    const showValidationErrors = ref(false);
    const showClosedNotice = ref(false);
    const closedNoticeAcknowledged = ref(false);
    const openState = ref<NextOpeningInfo | null>(null);
    const isEditRequest = computed(() => Boolean(currentRequest.value));

    const currentReservationSummary = computed(() => {
      if (!currentRequest.value) return null;
      const timezoneName = shop.value?.timezone || "UTC";
      const activeTime =
        currentRequest.value.reservation?.time ||
        currentRequest.value.requested_time;

      return {
        date: formatDateInTimezone(activeTime, timezoneName),
        time: formatTimeInTimezone(activeTime, timezoneName),
        partySize: currentRequest.value.party_size,
      };
    });

    const requestedReservationSummary = computed(() => {
      if (!reservationForm.selectedDate || !reservationForm.selectedTime) {
        return null;
      }

      const timezoneName = shop.value?.timezone || "UTC";
      const year = reservationForm.selectedDate.getFullYear();
      const month = String(
        reservationForm.selectedDate.getMonth() + 1,
      ).padStart(2, "0");
      const day = String(reservationForm.selectedDate.getDate()).padStart(
        2,
        "0",
      );
      const requestedTime = dayjs
        .tz(
          `${year}-${month}-${day}T${reservationForm.selectedTime}:00`,
          timezoneName,
        )
        .utc()
        .toISOString();

      return {
        date: formatDateInTimezone(requestedTime, timezoneName),
        time: formatTimeInTimezone(requestedTime, timezoneName),
        partySize: reservationForm.partySize,
      };
    });
    const requiresNewTimeSelection = computed(
      () => isEditRequest.value && !reservationForm.selectedTime,
    );
    const isSubmitDisabled = computed(
      () =>
        reservationForm.isSubmitting ||
        validationErrors.value.length > 0 ||
        requiresNewTimeSelection.value,
    );

    const handleDateSelect = (date: Date) => {
      reservationForm.selectedDate = date;
      reservationForm.selectedService = null;
      reservationForm.selectedTime = "";
      closedNoticeAcknowledged.value = false;
    };

    // The out-of-hours notice is acknowledged for one specific selection
    // only — once the guest picks a different time (directly, or indirectly
    // via handleDateSelect clearing it), the notice must be able to
    // reappear for the new selection.
    watch(
      () => reservationForm.selectedTime,
      () => {
        closedNoticeAcknowledged.value = false;
      },
    );

    const handleSubmit = async () => {
      if (validationErrors.value.length > 0) {
        showValidationErrors.value = true;
        return;
      }

      // Check if user is logged in
      if (!customer.value?.id) {
        showAuthDialog(reservationForm.email || undefined);
        return;
      }

      // Informational gate: warn (but don't block) when the restaurant is
      // currently outside its reservation hours.
      if (!closedNoticeAcknowledged.value) {
        const state = getReservationOpenState(
          reservationSchedule.value,
          new Date(),
          shop.value?.timezone || "UTC",
        );
        if (!state.openNow) {
          openState.value = state;
          showClosedNotice.value = true;
          return;
        }
      }

      // Ensure a date is selected before proceeding
      const selectedDateValue = reservationForm.selectedDate;
      if (!selectedDateValue) {
        reservationForm.submitError = t(
          "customer.reserve.validation_date_required",
        );
        return;
      }

      reservationForm.submitError = null;
      reservationForm.isSubmitting = true;
      try {
        // Get the restaurant's timezone (default to UTC if not set)
        const restaurantTimezone = shop.value?.timezone || "UTC";

        // Format the selected date as YYYY-MM-DD in local timezone
        const year = selectedDateValue.getFullYear();
        const month = String(selectedDateValue.getMonth() + 1).padStart(2, "0");
        const day = String(selectedDateValue.getDate()).padStart(2, "0");
        const dateStr = `${year}-${month}-${day}`;

        // Create a datetime string in the restaurant's timezone
        const dateTimeStr = `${dateStr}T${reservationForm.selectedTime}:00`;

        // Parse the datetime in the restaurant's timezone and convert to UTC
        const dateTimeInRestaurantTz = dayjs.tz(
          dateTimeStr,
          restaurantTimezone,
        );
        const dateTimeUTC = dateTimeInRestaurantTz.utc().toISOString();

        const depositRequired =
          prepaymentAmount.value && prepaymentAmount.value > 0;

        // If deposit is required, redirect to payment
        if (depositRequired) {
          try {
            const { checkoutId } =
              await createIntegratedWorkflowMutation.mutateAsync({
                requestedTime: dateTimeUTC,
                partySize: reservationForm.partySize,
                durationMins: reservationDurationMinutes.value,
                notes: reservationForm.notes || undefined,
                partyName: reservationForm.name,
                partyEmail: reservationForm.email,
                partyPhone: reservationForm.phone,
                tagIds:
                  reservationForm.selectedTagIds.length > 0
                    ? reservationForm.selectedTagIds
                    : undefined,
                requestKind: currentRequest.value ? "edit" : undefined,
                targetReservationId:
                  currentRequest.value?.reservation_id || undefined,
              });
            router.push(
              `/restaurant/${shopSlug.value}/checkout/${checkoutId}/payment-status`,
            );
            return;
          } catch (error) {
            console.error("Failed to initiate deposit:", error);
            reservationForm.submitError = t(
              "customer.reserve.deposit_init_failed",
            );
            return;
          }
        }

        const reservationRequest = await createReservationRequest({
          shopSlug: shopSlug.value,
          customer_id: customer.value.id,
          requested_time: dateTimeUTC,
          party_size: reservationForm.partySize,
          duration_mins: reservationDurationMinutes.value,
          notes: reservationForm.notes || undefined,
          party_name: reservationForm.name,
          party_email: reservationForm.email,
          party_phone: reservationForm.phone,
          tag_ids:
            reservationForm.selectedTagIds.length > 0
              ? reservationForm.selectedTagIds
              : undefined,
          request_kind: currentRequest.value ? "edit" : undefined,
          target_reservation_id:
            currentRequest.value?.reservation_id || undefined,
        });

        // No deposit — navigate to the reservation status page
        router.push({
          path: `/restaurant/${shopSlug.value}/reserve/reservation-request/${reservationRequest.id}`,
        });
      } catch (error) {
        console.error("Error submitting reservation:", error);
        reservationForm.submitError = t("customer.reserve.submit_failed");
      } finally {
        reservationForm.isSubmitting = false;
      }
    };

    const closeClosedNotice = () => {
      showClosedNotice.value = false;
    };

    const submitAnyway = () => {
      closedNoticeAcknowledged.value = true;
      showClosedNotice.value = false;
      handleSubmit();
    };

    const closedOpensAtCopy = computed(() => {
      const info = openState.value;
      if (!info?.opensAt) return null;
      return info.opensAt.isToday
        ? t("customer.reserve.closed_opens_today", [info.opensAt.time])
        : t("customer.reserve.closed_opens_on", [
            t(`customer.reserve.weekday_${info.opensAt.weekday}`),
            info.opensAt.time,
          ]);
    });

    return () => (
      <div>
        {showClosedNotice.value && (
          <LemmonModal
            title={t("customer.reserve.closed_notice_title")}
            onClose={closeClosedNotice}
            renderButtons={() => (
              <div class="flex gap-2 p-4">
                <button
                  type="button"
                  onClick={closeClosedNotice}
                  class="btn btn-secondary flex-1"
                >
                  {t("customer.reserve.closed_pick_other")}
                </button>
                <button
                  type="button"
                  onClick={submitAnyway}
                  disabled={reservationForm.isSubmitting}
                  class="btn btn-accent flex-1"
                >
                  {t("customer.reserve.closed_submit_anyway")}
                </button>
              </div>
            )}
          >
            <div class="p-6 space-y-3">
              <p class="text-sm text-gray-700">
                {t("customer.reserve.closed_notice_body")}
              </p>
              {closedOpensAtCopy.value && (
                <p class="hidden text-sm text-gray-700">{closedOpensAtCopy.value}</p>
              )}
              <p class="text-sm text-gray-500">
                {t("customer.reserve.closed_response_hint")}
              </p>
            </div>
          </LemmonModal>
        )}
        {shop.value ? (
          <div class="space-y-8">
            {/* Date & Time Selection — contained card */}
            {reservationForm.selectedDate && (
              <div class="rounded-xl border border-gray-100 bg-gray-50/50 p-5 md:p-6">
                <div class="flex flex-col md:flex-row gap-6">
                  {/* Left: Calendar */}
                  <div class="flex-shrink-0">
                    <Calendar
                      selectedDate={reservationForm.selectedDate}
                      onDateSelect={handleDateSelect}
                      timezone={shop.value?.timezone || "UTC"}
                      compact
                    />
                  </div>

                  {/* Right: Guests + Time */}
                  <div class="flex-1 space-y-5">
                    <PartySizeSelector
                      modelValue={reservationForm.partySize}
                      min={minPartySize.value}
                      onUpdate:modelValue={(val: number) =>
                        (reservationForm.partySize = val)
                      }
                      compact
                    />

                    <ServiceTimeSelector
                      selectedDate={reservationForm.selectedDate}
                      selectedService={reservationForm.selectedService}
                      selectedTime={reservationForm.selectedTime}
                      schedule={reservationSchedule.value}
                      slotMinutes={timeSlotMinutes.value}
                      shopSlug={shopSlug.value}
                      targetPartySize={reservationForm.partySize}
                      timezone={shop.value?.timezone || "UTC"}
                      onUpdate:selectedService={(val: string | null) =>
                        (reservationForm.selectedService = val)
                      }
                      onUpdate:selectedTime={(val: string) =>
                        (reservationForm.selectedTime = val)
                      }
                      compact
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contact — clean, no heavy card */}
            {currentRequest.value && currentReservationSummary.value && (
              <div class="rounded-2xl border border-amber-200 bg-[linear-gradient(135deg,#fff8e8_0%,#fff3d4_100%)] p-5 md:p-6">
                <div class="flex items-start gap-3">
                  <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 text-amber-700 shadow-sm">
                    <span class="material-symbols-outlined text-[20px]">
                      schedule
                    </span>
                  </div>
                  <div class="min-w-0 flex-1 space-y-3">
                    <div class="space-y-1">
                      <h3 class="text-base font-medium text-amber-950">
                        {t("customer.reserve.change_request")}
                      </h3>
                      <p class="text-sm text-amber-900/80">
                        {t("customer.reserve.change_request_copy")}
                      </p>
                    </div>

                    <div class="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                      <div class="rounded-xl bg-white/70 px-4 py-3">
                        <p class="text-xs font-medium uppercase tracking-[0.12em] text-amber-700/80">
                          {t("customer.reserve.current")}
                        </p>
                        <p class="mt-1 text-sm font-medium text-amber-950">
                          {currentReservationSummary.value.date}
                        </p>
                        <p class="text-sm text-amber-900/80">
                          {currentReservationSummary.value.time}
                        </p>
                      </div>

                      <div class="flex items-center justify-center text-amber-700">
                        <span class="material-symbols-outlined text-[18px]">
                          east
                        </span>
                      </div>

                      <div class="rounded-xl border border-dashed border-amber-300 bg-white/60 px-4 py-3">
                        <p class="text-xs font-medium uppercase tracking-[0.12em] text-amber-700/80">
                          {t("customer.reserve.new")}
                        </p>
                        {requestedReservationSummary.value ? (
                          <>
                            <p class="mt-1 text-sm font-medium text-amber-950">
                              {requestedReservationSummary.value.date}
                            </p>
                            <p class="text-sm text-amber-900/80">
                              {requestedReservationSummary.value.time}
                            </p>
                          </>
                        ) : (
                          <p class="mt-1 text-sm text-amber-900/80">
                            {t("customer.reserve.pick_new_time")}
                          </p>
                        )}
                      </div>
                    </div>

                    {requestedReservationSummary.value && (
                      <p class="text-sm text-amber-900/80">
                        {t("customer.reserve.new_time_at", [
                          requestedReservationSummary.value.date,
                          requestedReservationSummary.value.time,
                        ])}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {currentRequestError.value && (
              <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                {currentRequestError.value}
              </div>
            )}

            <div>
              <h3 class="text-base font-medium text-gray-900 mb-4">
                {t("customer.reserve.your_details")}
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label
                    for="reservation-name"
                    class="text-sm font-medium text-gray-700"
                  >
                    {t("customer.reserve.full_name_required")}
                  </label>
                  <input
                    id="reservation-name"
                    type="text"
                    data-testid="input-guest-name"
                    value={reservationForm.name}
                    onInput={(e) =>
                      (reservationForm.name = (
                        e.target as HTMLInputElement
                      ).value)
                    }
                    placeholder="Marie Dubois"
                    aria-label={t("customer.reserve.full_name")}
                    class="input input-bordered w-full bg-white focus:input-primary"
                    required
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label
                    for="reservation-email"
                    class="text-sm font-medium text-gray-700"
                  >
                    {t("customer.reserve.email_required")}
                  </label>
                  <input
                    id="reservation-email"
                    type="email"
                    data-testid="input-guest-email"
                    value={reservationForm.email}
                    onInput={(e) =>
                      (reservationForm.email = (
                        e.target as HTMLInputElement
                      ).value)
                    }
                    placeholder="marie@exemple.ch"
                    aria-label={t("customer.reserve.email_address")}
                    class={`input input-bordered w-full bg-white focus:input-primary ${
                      emailTypoSuggestion.value ? "input-warning" : ""
                    }`}
                    required
                  />
                  {emailTypoSuggestion.value && (
                    <p class="text-xs text-error" role="alert">
                      {t("customer.reserve.validation_email_typo", [
                        emailTypoSuggestion.value,
                      ])}
                    </p>
                  )}
                </div>
                <div class="flex flex-col gap-1.5">
                  <label
                    for="reservation-phone"
                    class="text-sm font-medium text-gray-700"
                  >
                    {t("customer.reserve.phone_required")}
                  </label>
                  <input
                    id="reservation-phone"
                    type="tel"
                    data-testid="input-guest-phone"
                    value={reservationForm.phone}
                    onInput={(e) =>
                      (reservationForm.phone = (
                        e.target as HTMLInputElement
                      ).value)
                    }
                    placeholder="+41 79 123 45 67"
                    aria-label={t("customer.reserve.phone_number")}
                    class="input input-bordered w-full bg-white focus:input-primary"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Special requests + Tags */}
            <div>
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-base font-medium text-gray-900">
                  {t("customer.reserve.anything_else")}
                </h3>
                <span class="text-xs text-gray-400">
                  {t("customer.reserve.optional")}
                </span>
              </div>

              {/* Tags */}
              {availableTags.value.length > 0 && (
                <div class="flex flex-wrap gap-2 mb-3">
                  {availableTags.value.map((tag) => {
                    const isSelected = reservationForm.selectedTagIds.includes(
                      tag.id,
                    );
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        aria-pressed={isSelected}
                        class={`flex items-center gap-1.5 px-4 min-h-[44px] rounded-full text-sm border ${
                          isSelected
                            ? "border-transparent shadow-sm hover:opacity-90"
                            : "border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 bg-white"
                        }`}
                        style={{
                          ...(isSelected
                            ? {
                                backgroundColor: tag.color || "#1a1a1a",
                                color: getTagTextColor(tag.color || "#1a1a1a"),
                                borderColor: "transparent",
                              }
                            : {}),
                          transition:
                            "background-color 200ms ease, color 200ms ease, border-color 200ms ease, opacity 150ms ease",
                        }}
                      >
                        {tag.icon && (
                          <span
                            class="material-symbols-outlined text-sm"
                            aria-hidden="true"
                          >
                            {tag.icon}
                          </span>
                        )}
                        <span>{tag.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Notes textarea */}
              <textarea
                value={reservationForm.notes}
                onInput={(e) =>
                  (reservationForm.notes = (
                    e.target as HTMLTextAreaElement
                  ).value)
                }
                placeholder={t("customer.reserve.notes_placeholder")}
                class="textarea textarea-bordered w-full bg-white focus:textarea-primary resize-none text-sm"
                rows={3}
              />
              <div class="mt-0 text-xs text-gray-400 w-full">
                {t("customer.reserve.notes_disclaimer")}
              </div>
            </div>

            {/* Prepayment Notice */}
            {prepaymentAmount.value && prepaymentAmount.value > 0 && (
              <div class="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded-lg px-4 py-3">
                <span class="material-symbols-outlined text-base">info</span>
                <span>
                  {t("customer.reserve.prepayment_of")}{" "}
                  <span class="font-medium">
                    {formatPrice(
                      prepaymentAmount.value,
                      true,
                      2,
                      shop.value?.currency,
                    )}
                  </span>{" "}
                  {t("customer.reserve.deposit_no_show")}
                </span>
              </div>
            )}

            {/* Validation Errors */}
            {showValidationErrors.value &&
              validationErrors.value.length > 0 && (
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div class="flex items-start gap-2">
                    <span class="material-symbols-outlined text-red-600 text-base mt-0.5">
                      error
                    </span>
                    <div class="flex-1">
                      <p class="text-sm font-medium text-red-800 mb-2">
                        {t("customer.reserve.fix_errors")}
                      </p>
                      <ul class="list-disc list-inside space-y-1">
                        {validationErrors.value.map((error, index) => (
                          <li key={index} class="text-sm text-red-700">
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

            {/* Reserve button */}
            <button
              type="button"
              data-testid="btn-submit-request"
              onClick={handleSubmit}
              disabled={isSubmitDisabled.value}
              class={`btn-accent btn-lg w-full rounded-xl text-base py-3.5 font-medium ${isSubmitDisabled.value && !reservationForm.isSubmitting ? "!bg-gray-100 !text-gray-400 !shadow-none !transform-none cursor-not-allowed" : "cursor-pointer"}`}
            >
              {reservationForm.isSubmitting
                ? isEditRequest.value
                  ? t("customer.reserve.sending_request")
                  : t("customer.reserve.reserving")
                : prepaymentAmount.value && prepaymentAmount.value > 0
                  ? isEditRequest.value
                    ? t("customer.reserve.send_request_deposit", [
                        formatPrice(
                          prepaymentAmount.value,
                          true,
                          2,
                          shop.value?.currency,
                        ),
                      ])
                    : t("customer.reserve.reserve_table_deposit", [
                        formatPrice(
                          prepaymentAmount.value,
                          true,
                          2,
                          shop.value?.currency,
                        ),
                      ])
                  : isEditRequest.value
                    ? t("customer.reserve.send_request")
                    : t("customer.reserve.reserve_table")}
            </button>
            {reservationForm.submitError && (
              <div class="bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200">
                {reservationForm.submitError}
              </div>
            )}
          </div>
        ) : (
          <div class="flex items-center justify-center py-8">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        )}
      </div>
    );
  },
});
